/**
 * Auth bootstrap hook.
 *
 * Calls `GET /api/v1/auth/me` exactly once per Clerk sign-in so the
 * backend's M0.6 first-login upsert creates the local `User` row.
 * Without this call, every other endpoint returns 401 with
 * "No local user record. Call GET /api/v1/auth/me before other
 * endpoints."
 *
 * The shell gates the rest of the app on this query resolving so
 * downstream `useQuery` calls (tasks, leads, opps, morning brief)
 * never race the upsert.
 */

import { useAuth } from '@clerk/clerk-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '../api-client';

export interface AuthMe {
  id: string;
  email: string;
  name: string;
}

export const AUTH_ME_KEY = ['auth', 'me'] as const;

export function useAuthMe(): UseQueryResult<AuthMe, Error> {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: AUTH_ME_KEY,
    enabled: isSignedIn === true,
    staleTime: Infinity,
    retry: 1,
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('No active Clerk session');
      return apiRequest<AuthMe>('/api/v1/auth/me', { token });
    },
  });
}
